import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { adminApi } from "@/api/admin.api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { formatMoney } from "./first-round";
import { useAdminProduct } from "./hooks";
import { type Column, DataTable } from "./ui/DataTable";
import { PageHeader } from "./ui/PageHeader";

const usageColumns: Column<any>[] = [
  { key: "name", header: "Feature", sortValue: (s) => s.name, cell: (s) => <span className="font-medium">{s.name}</span> },
  { key: "total", header: "Uses", className: "text-right tabular-nums", sortValue: (s) => s.total, cell: (s) => s.total },
  {
    key: "success",
    header: "Success rate",
    className: "text-right tabular-nums",
    sortValue: (s) => (s.total ? s.successRate : null),
    cell: (s) => (s.total ? `${Math.round((s.successRate || 0) * 100)}%` : "—"),
  },
];

export function Analytics() {
  const { product } = useAdminProduct();
  const [range, setRange] = useState<{ start: string; end: string }>(() => {
    const now = new Date();
    const end = now.toISOString();
    const start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    return { start, end };
  });

  const { data: revenue } = useQuery({ queryKey: ["revenue", product], queryFn: () => adminApi.getRevenue(product, "monthly") });
  const { data: featureUsage, isLoading, error } = useQuery({
    queryKey: ["feature-usage", product, range],
    queryFn: () => adminApi.getFeatureUsage(product, range.start, range.end),
  });
  const { data: churn } = useQuery({ queryKey: ["churn", product], queryFn: () => adminApi.getChurn(product) });

  return (
    <div className="max-w-5xl space-y-8">
      <PageHeader title="Analytics" description="Subscription revenue, churn and feature usage for the selected product." />
      <div className="grid gap-4 sm:grid-cols-2">
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-slate-500">Estimated MRR</div>
            <div className="mt-1 text-2xl font-semibold tabular-nums">{formatMoney(revenue?.mrr || 0)}</div>
            <div className="mt-2 text-xs text-slate-500">Monthly list price of each active subscription.</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-slate-500">Churn (all time)</div>
            <div className="mt-1 text-2xl font-semibold tabular-nums">{Math.round((churn?.churnRate || 0) * 100)}%</div>
            <div className="mt-2 text-xs text-slate-500">Cancelled subscriptions out of active plus cancelled.</div>
          </CardContent>
        </Card>
      </div>
      <section>
        <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
          <h2 className="font-medium">Feature usage</h2>
          <div className="flex items-center gap-2 text-sm">
            <Input className="w-auto" type="datetime-local" aria-label="From" value={toLocalDT(range.start)} onChange={(e) => setRange((r) => ({ ...r, start: new Date(e.target.value).toISOString() }))} />
            <span className="text-slate-500">to</span>
            <Input className="w-auto" type="datetime-local" aria-label="To" value={toLocalDT(range.end)} onChange={(e) => setRange((r) => ({ ...r, end: new Date(e.target.value).toISOString() }))} />
          </div>
        </div>
        <DataTable
          label="Feature usage"
          columns={usageColumns}
          rows={featureUsage}
          rowKey={(s) => s.featureKey}
          isLoading={isLoading}
          error={error}
          emptyMessage="No features for this product."
          pageSize={null}
          initialSort={{ key: "total", direction: "desc" }}
        />
      </section>
    </div>
  );
}

function toLocalDT(iso: string) {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
